/**
 * BookingDetailMuiUI.jsx
 * Module: common / Booking Detail
 *
 * The MUI design system for the booking-detail surfaces: amber-accented section
 * cards, KPI tiles, colour-railed charge groups and amber-header tables.
 *
 * These primitives started life inside pages/BookingDetailPage.jsx (air). They
 * live here now because the ocean detail view (components/BookingHistory/
 * OceanCargoDetails.jsx) renders the same design, and duplicating ~400 lines of
 * styling across the two surfaces is exactly how they drifted apart the first
 * time. Air-specific logic (volume-weight maths, carrier labels, markup tiles)
 * deliberately stays in the page — only the look lives here.
 *
 * Sibling module: BookingDetailUI.jsx holds the pure field resolvers and
 * formatting helpers (safeNum, getFlag, resolveStoredCommodity, …) that every
 * booking-detail surface shares.
 *
 * Author: FreightSee frontend team
 */
import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';

// ─── Brand colour constants ────────────────────────────────────────────────
export const C = {
  amber:       '#F59E0B',
  amberDark:   '#D97706',
  amberDim:    '#B45309',
  amberTint:   '#FFFBEB',
  amberLight:  '#FEF3C7',
  amberBorder: '#FDE68A',
  green:       '#16A34A', greenTint:  '#DCFCE7',
  red:         '#DC2626', redTint:    '#FEE2E2', redBorder: '#FECACA',
  blue:        '#2563EB', blueTint:   '#DBEAFE',
  teal:        '#0D9488', tealTint:   '#F0FDFA', tealBorder: '#99F6E4',
  slate:       '#475569', slateTint:  '#F8FAFC', slateBorder: '#CBD5E1',
};

/**
 * Maps a booking status onto its pill colours. Matching is on lowercased
 * substrings (not an exact enum) because the backend has used several status
 * strings over time — "Booking Confirmed", "Confirmation Awaited", "Cancelled".
 * Mirrors StatusBadge in BookingDetailUI.jsx so every booking surface agrees on
 * what green/amber/red mean.
 *
 * The pre-booking query statuses this page also renders ("Quote Available",
 * "Live Rates Available", "Quote Expired" — see resolveQueryStatus in
 * pages/BookingDetailPage.jsx) are matched here too, so a query opened in
 * query mode gets a meaningful pill instead of the neutral grey fallback.
 * "expire" is tested before "cancel"/"reject" and "available"/"quoted" after
 * "pending"/"awaited", so "Quote Pending" stays amber rather than turning blue.
 *
 * @param {string} status - raw status string from the booking
 * @returns {{bg: string, fg: string, dot: string}} pill background / text / dot colours
 */
export const statusStyle = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('success') || s.includes('confirmed')) return { bg: C.greenTint,  fg: C.green,    dot: C.green };
  if (s.includes('expire')) return { bg: C.redTint, fg: C.red, dot: C.red };
  if (s.includes('pending') || s.includes('awaited'))   return { bg: C.amberLight, fg: C.amberDim, dot: C.amber };
  if (s.includes('reject') || s.includes('cancel') || s.includes('fail'))
    return { bg: C.redTint, fg: C.red, dot: C.red };
  if (s.includes('available') || s.includes('quoted')) return { bg: C.blueTint, fg: C.blue, dot: C.blue };
  return { bg: '#F5F5F4', fg: '#57534E', dot: '#A8A29E' };
};

// ─── StatusPill ─────────────────────────────────────────────────────────────
/**
 * Dot + label status pill, coloured by statusStyle. Used in the page top bar
 * and wherever a booking/payment status is surfaced inline.
 * @param {{status: string}} props
 * @returns {JSX.Element|null} the pill, or null when there is no status
 */
export const StatusPill = ({ status }) => {
  if (!status) return null;
  const s = statusStyle(status);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        px: '10px', py: '3px', borderRadius: '99px',
        fontSize: '10px', fontWeight: 700, letterSpacing: '.04em',
        textTransform: 'uppercase',
        bgcolor: s.bg, color: s.fg,
      }}
    >
      <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: s.dot, display: 'inline-block' }} />
      {status}
    </Box>
  );
};

// ─── SectionCard ────────────────────────────────────────────────────────────
/**
 * Card shell with an amber icon badge, uppercase title and optional count
 * badge, wrapping every major section of the page.
 *
 * `badge` is the small grey count chip ("3 legs"); `headerRight` is for a node
 * that brings its own styling and should sit at the right of the header
 * unwrapped — the ocean view puts its booking-status pill there. Both can be
 * used together, badge first.
 *
 * @param {{icon: React.ReactNode, title: string, badge?: React.ReactNode,
 *   headerRight?: React.ReactNode, children: React.ReactNode}} props
 */
export const SectionCard = ({ icon, title, badge, headerRight, children }) => {
  const isDark = useTheme().palette.mode === 'dark';

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1.5px solid ${isDark ? '#2C2825' : '#E7E5E4'}`,
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: isDark ? '0 1px 3px rgba(0,0,0,.3)' : '0 1px 3px rgba(28,25,23,.06)',
      }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: '13px', py: '9px',
          borderBottom: `1.5px solid ${isDark ? '#2C2825' : '#E7E5E4'}`,
          bgcolor: isDark ? '#211E1B' : '#FAFAF9',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            sx={{
              width: 24, height: 24, bgcolor: C.amber, borderRadius: '5px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, '& svg': { fontSize: 14, color: '#fff' },
            }}
          >
            {icon}
          </Box>
          <Typography
            sx={{
              fontSize: '11px', fontWeight: 700,
              color: isDark ? '#F5F5F4' : '#1C1917',
              letterSpacing: '.04em', textTransform: 'uppercase',
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {badge && (
            <Typography
              component="span"
              sx={{
                fontSize: '10px', fontWeight: 600,
                color: isDark ? '#A8A29E' : '#78716C',
                bgcolor: isDark ? '#171412' : '#F5F5F4',
                border: `1px solid ${isDark ? '#2C2825' : '#E7E5E4'}`,
                borderRadius: '99px', px: '7px', py: '1px',
              }}
            >
              {badge}
            </Typography>
          )}
          {headerRight}
        </Box>
      </Box>

      <Box sx={{ p: '12px 13px' }}>{children}</Box>
    </Paper>
  );
};

// ─── InfoSubLabel ───────────────────────────────────────────────────────────
/**
 * Full-width amber sub-heading with a trailing rule, used to split the
 * Booking Information grid into Route / Commodity Details / Booking Details.
 * @param {{children: React.ReactNode, isFirst?: boolean}} props
 */
export const InfoSubLabel = ({ children, isFirst = false }) => (
  <Box
    sx={{
      gridColumn: '1 / -1',
      display: 'flex', alignItems: 'center', gap: '6px',
      fontSize: '9px', fontWeight: 700, letterSpacing: '.09em',
      textTransform: 'uppercase', color: C.amberDark,
      pt: isFirst ? 0 : '8px', pb: '5px',
      borderTop: isFirst ? 'none' : '1px solid #F0EFEE',
      '&::after': { content: '""', flex: 1, height: '1px', background: C.amberBorder, opacity: .5 },
    }}
  >
    {children}
  </Box>
);

// ─── InfoCell ───────────────────────────────────────────────────────────────
/**
 * Label/value pair inside the Booking Information grid. Long values wrap
 * rather than truncate — SHC lists and commodity descriptions are the point of
 * the cell, so hiding them behind an ellipsis would lose real information.
 * @param {{label: string, value: React.ReactNode}} props
 */
export const InfoCell = ({ label, value }) => {
  const isDark = useTheme().palette.mode === 'dark';

  return (
    <Box sx={{ pb: '10px', pr: '14px', minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '9px', fontWeight: 600,
          color: isDark ? '#78716C' : '#A8A29E',
          letterSpacing: '.05em', textTransform: 'uppercase', mb: '2px',
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontSize: '13px', fontWeight: 600,
          color: isDark ? '#F5F5F4' : '#1C1917',
          fontVariantNumeric: 'tabular-nums',
          overflowWrap: 'anywhere', lineHeight: 1.35,
        }}
      >
        {value ?? 'N/A'}
      </Typography>
    </Box>
  );
};

// ─── StatCard ───────────────────────────────────────────────────────────────
/**
 * KPI tile. `highlight` calls out the figure that drives billing (chargeable
 * weight on air, total rate on ocean) with the amber accent.
 * @param {{label: string, value: React.ReactNode, unit?: string, highlight?: boolean}} props
 */
export const StatCard = ({ label, value, unit, highlight = false }) => {
  const isDark = useTheme().palette.mode === 'dark';

  return (
    <Box
      sx={{
        bgcolor: highlight ? (isDark ? '#1C1610' : C.amberTint) : (isDark ? '#211E1B' : '#FAFAF9'),
        border: `1.5px solid ${highlight ? C.amberBorder : (isDark ? '#2C2825' : '#E7E5E4')}`,
        borderRadius: '10px',
        p: '9px 9px 7px',
      }}
    >
      <Typography
        sx={{
          fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em',
          color: highlight ? C.amberDark : (isDark ? '#78716C' : '#A8A29E'),
          mb: '4px',
        }}
      >
        {label}
      </Typography>
      {/* Value + unit share one baseline-aligned row so every tile is the same
          height regardless of whether it carries a unit. */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px' }}>
        <Typography
          sx={{
            fontSize: '14px', fontWeight: 800,
            color: highlight ? C.amber : (isDark ? '#F5F5F4' : '#1C1917'),
            letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
          }}
        >
          {value ?? 'N/A'}
        </Typography>
        {unit && (
          <Typography
            component="span"
            sx={{
              fontSize: '9px', fontWeight: 600,
              color: isDark ? '#78716C' : '#A8A29E',
              textTransform: 'uppercase', letterSpacing: '.04em', lineHeight: 1.1,
            }}
          >
            {unit}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ─── TaxableRow ─────────────────────────────────────────────────────────────
/**
 * Dashed amber "Taxable Amount" divider that precedes the GST block, so the
 * breakdown reads: charge groups → Taxable Amount → GST → Net Amount, matching
 * the checkout layout in NewBookingFlowContent.jsx.
 * @param {{amount: React.ReactNode}} props
 */
export const TaxableRow = ({ amount }) => {
  const isDark = useTheme().palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: '10px', py: '6px', mb: '5px',
        bgcolor: isDark ? '#2C1F05' : C.amberLight,
        border: `1.5px dashed ${C.amberBorder}`,
        borderRadius: '6px',
      }}
    >
      <Typography sx={{ fontSize: '9px', fontWeight: 700, color: C.amberDim, textTransform: 'uppercase', letterSpacing: '.07em' }}>
        Taxable Amount
      </Typography>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: C.amberDark, fontVariantNumeric: 'tabular-nums' }}>
        {amount}
      </Typography>
    </Box>
  );
};

// ─── ChargeGroup ────────────────────────────────────────────────────────────
/**
 * One charge category (Main, Fuel, GST, Origin Charges …) as a colour-railed
 * block: header with the category subtotal, then its line items. GST gets the
 * teal rail so the tax block reads apart from the freight charges above it.
 *
 * `total` and each item's `amount` are ReactNodes, not strings, so a caller can
 * pass a dual-currency figure (ocean shows "$ 1,200.00 (₹ 1,00,000.00)").
 *
 * @param {{name: string, total: React.ReactNode, items?: Array<{name: React.ReactNode, rate?: React.ReactNode, amount: React.ReactNode}>, isGst?: boolean, note?: React.ReactNode}} props
 */
export const ChargeGroup = ({ name, total, items = [], isGst = false, note }) => {
  const isDark = useTheme().palette.mode === 'dark';

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${isGst ? C.teal : C.amber}`,
        borderRadius: '0 6px 6px 0',
        overflow: 'hidden', mb: '5px',
      }}
    >
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: '10px', py: '6px',
          bgcolor: isGst ? (isDark ? '#042F2E' : C.tealTint) : (isDark ? '#1C1610' : C.amberTint),
          borderBottom: `1px solid ${isGst ? C.tealBorder : C.amberBorder}`,
        }}
      >
        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: isGst ? C.teal : C.amberDim, textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {name}
        </Typography>
        <Typography component="div" sx={{ fontSize: '11px', fontWeight: 700, color: isGst ? C.teal : C.amberDark, fontVariantNumeric: 'tabular-nums' }}>
          {total}
        </Typography>
      </Box>

      {/* Explanatory footnote under the header (ocean's GST split rule). */}
      {note && (
        <Box sx={{ px: '10px', py: '5px', bgcolor: isDark ? '#1C1917' : '#fff' }}>
          <Typography sx={{ fontSize: '10px', color: isDark ? '#78716C' : '#A8A29E' }}>
            {note}
          </Typography>
        </Box>
      )}

      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: '10px', py: '6px',
            bgcolor: isDark ? '#1C1917' : '#fff',
            borderBottom: idx < items.length - 1 ? `1px solid ${isDark ? '#252220' : '#F0EFEE'}` : 'none',
            transition: 'background .1s',
            '&:hover': { bgcolor: isDark ? '#211E1B' : '#FAFAF9' },
          }}
        >
          <Box sx={{ minWidth: 0, pr: '10px' }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: isDark ? '#A8A29E' : '#57534E', textTransform: 'uppercase', letterSpacing: '.03em', lineHeight: 1.3 }}>
              {item.name}
            </Typography>
            {item.rate && (
              <Typography sx={{ fontSize: '10px', color: isDark ? '#78716C' : '#A8A29E', mt: '1px' }}>
                {item.rate}
              </Typography>
            )}
          </Box>
          <Typography component="div" sx={{ fontSize: '11px', fontWeight: 700, color: isDark ? '#F5F5F4' : '#1C1917', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {item.amount}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── NetAmountBar ───────────────────────────────────────────────────────────
/**
 * Solid amber bar that closes a charge breakdown.
 * @param {{label?: string, amount: React.ReactNode}} props
 */
export const NetAmountBar = ({ label = 'Net Amount', amount }) => (
  <Box
    sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      mt: '7px', px: '12px', py: '9px',
      bgcolor: C.amber, borderRadius: '6px', gap: '10px',
    }}
  >
    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,.88)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
      {label}
    </Typography>
    <Typography component="div" sx={{ fontSize: '15px', fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums', letterSpacing: '-.02em', textAlign: 'right' }}>
      {amount}
    </Typography>
  </Box>
);

// ─── Pills ──────────────────────────────────────────────────────────────────

/** Route-banner tag chip (commodity, temperature, product codes, DG). */
export const TagChip = ({ children, tone = 'amber', title }) => {
  const isDark = useTheme().palette.mode === 'dark';
  const tones = {
    amber: { bg: C.amberLight, fg: C.amberDim, bd: C.amberBorder },
    teal:  { bg: isDark ? '#042F2E' : C.tealTint, fg: C.teal, bd: C.tealBorder },
    red:   { bg: C.redTint, fg: C.red, bd: C.redBorder },
    plain: { bg: isDark ? '#1C1917' : '#fff', fg: isDark ? '#F5F5F4' : '#57534E', bd: isDark ? '#2C2825' : '#E7E5E4' },
  };
  const t = tones[tone] || tones.amber;

  return (
    <Box
      component="span"
      title={title}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        maxWidth: '260px', px: '7px', py: '1px', borderRadius: '99px',
        fontSize: '10px', fontWeight: 600, letterSpacing: '.03em',
        bgcolor: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}
    >
      {children}
    </Box>
  );
};

/** Departure/arrival segment pill in the Flight/Schedule Details tables. */
export const SegmentPill = ({ label, variant }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block', px: '8px', py: '1px', borderRadius: '99px',
      fontSize: '11px', fontWeight: 700,
      bgcolor: variant === 'dep' ? C.blueTint : C.greenTint,
      color:   variant === 'dep' ? C.blue     : C.green,
    }}
  >
    {label}
  </Box>
);

/** Yes/No pill for the turnable / stackable columns. */
export const BoolPill = ({ value }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block', px: '8px', py: '1px', borderRadius: '99px',
      fontSize: '11px', fontWeight: 700,
      bgcolor: value ? C.greenTint : C.redTint,
      color:   value ? C.green     : C.red,
    }}
  >
    {value ? 'Yes' : 'No'}
  </Box>
);

// ─── Shared table cell sx ───────────────────────────────────────────────────
export const thSx = {
  fontSize: '9px', fontWeight: 700, color: 'rgba(255,255,255,.92)',
  textTransform: 'uppercase', letterSpacing: '.07em',
  whiteSpace: 'nowrap', bgcolor: C.amber, py: '7px', px: '10px', border: 'none',
};

export const tdSx = (isDark) => ({
  fontSize: '12px', py: '7px', px: '10px', whiteSpace: 'nowrap',
  color: isDark ? '#F5F5F4' : '#1C1917',
  fontVariantNumeric: 'tabular-nums', border: 'none',
  borderBottom: `1px solid ${isDark ? '#252220' : '#F0EFEE'}`,
});
